import { Box, Group, Text } from '@mantine/core'
import type { CSSProperties } from 'react'

import { HydrationSafe } from '../hooks/HydrationSafe'
import liveLabelStyles from './liveLabelStyles.module.css'

interface RecurringLiveSubtitleProps {
  /**
   * 距下一次到期的实时倒计时（如 `"5m 4s"`），已由 `useRecurringEventLabel`
   * 格式化好。缺省 → 只渲染 LIVE 徽标。
   */
  countdown?: string
  /** 倒计时 `<time>` 元素对应的 ISO 到期时间戳。 */
  datetime?: string
}

/**
 * Port of cdc-web `Compact/_shared/RecurringLiveSubtitle.tsx`：recurring
 * financial 卡片头的副标题（Figma 2466-105311）——红点 + `LIVE` + 倒计时。
 *
 * recurring 事件永远"在直播"（持续滚入下一个窗口），所以 LIVE 徽标恒亮；
 * 只有无法解析到期时间时倒计时段才消失。周期/时长标签不在这里——它上移到
 * 页头图标旁（替代 event-kind 名）。
 *
 * 复用共享的 pulsingDot keyframe + `--bg-status-danger-bold` token（6px 点，
 * 比 CompactLiveLabel 的 8px 小一号），LIVE 标记与 match 卡一致。
 */
export function RecurringLiveSubtitle({
  countdown,
  datetime,
}: Readonly<RecurringLiveSubtitleProps>) {
  return (
    <Group gap="2xs" align="center" wrap="nowrap" aria-label="Event is live">
      <Group gap="3xs" align="center" wrap="nowrap">
        <Box
          component="span"
          className={liveLabelStyles.pulsingDot}
          style={
            { '--dot-size': '6px', background: 'var(--bg-status-danger-bold)' } as CSSProperties
          }
          aria-hidden="true"
        />
        <Text component="span" variant="subtitle" c="var(--bg-status-danger-bold)">
          LIVE
        </Text>
      </Group>
      {countdown && (
        <HydrationSafe>
          <Text
            component="time"
            variant="subtitle"
            c="var(--content-secondary)"
            dateTime={datetime}
            suppressHydrationWarning
          >
            {countdown}
          </Text>
        </HydrationSafe>
      )}
    </Group>
  )
}
